import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '@/components/ui/table';
import { Plus, Home, Car, Edit, Trash2, ChevronLeft, Upload, X } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import extractErrorMessage from '@/utils/extractErrorMessage';

const formatCurrency = (amount) => new Intl.NumberFormat('id-ID', {
  style: 'currency', currency: 'IDR', minimumFractionDigits: 0
}).format(amount || 0).replace('Rp', 'Rp.');

const parseNumber = (val) => {
  if (val === '' || val === null || val === undefined) return 0;
  const digits = String(val).replace(/[^0-9]/g, '');
  return digits ? parseInt(digits, 10) : 0;
};

// Format a value with Indonesian thousand separators for display in inputs (e.g. 5000000 -> "5.000.000")
const formatThousand = (val) => {
  const digits = String(val ?? '').replace(/[^0-9]/g, '');
  if (!digits) return '';
  return new Intl.NumberFormat('id-ID').format(parseInt(digits, 10));
};

const Assets = ({ user, initialTab = 'rumah' }) => {
  const [rumahList, setRumahList] = useState([]);
  const [mobilList, setMobilList] = useState([]);
  const [selected, setSelected] = useState(null); // currently opened asset detail

  // Asset (header) dialog
  const [assetDialog, setAssetDialog] = useState({ open: false, type: 'RUMAH', editing: null });
  const [assetForm, setAssetForm] = useState({});
  const [carFiles, setCarFiles] = useState([]); // newly selected car image File objects
  const [uploadingImages, setUploadingImages] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [imageModal, setImageModal] = useState({ open: false, index: 0 });

  // Entry (row) dialog
  const [entryDialog, setEntryDialog] = useState({ open: false, editing: null });
  const [entryForm, setEntryForm] = useState({});

  const fetchAssets = async () => {
    try {
      const [rumahRes, mobilRes] = await Promise.all([
        api.get('/assets', { params: { type: 'RUMAH' } }),
        api.get('/assets', { params: { type: 'MOBIL' } }),
      ]);
      setRumahList(rumahRes.data);
      setMobilList(mobilRes.data);
      // keep selected asset in sync if open
      if (selected) {
        const all = [...rumahRes.data, ...mobilRes.data];
        const fresh = all.find(a => a.id === selected.id);
        if (fresh) setSelected(fresh);
      }
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Gagal memuat data asset'));
    }
  };

  useEffect(() => { fetchAssets(); }, []);

  // ---------- Asset header CRUD ----------
  const openAddAsset = (type) => {
    setCarFiles([]);
    setAssetForm(type === 'RUMAH'
      ? { name: '' }
      : { car_type: '', plat: '', masa_bulan: '', cicilan_per_bulan: '', harga_mobil: '', nama_ktp: '', nomor_ktp: '', nama_cabang: '', status: 'Berjalan', images: [] });
    setAssetDialog({ open: true, type, editing: null });
  };

  const openEditAsset = (asset) => {
    setCarFiles([]);
    setAssetForm({
      ...asset,
      masa_bulan: formatThousand(asset.masa_bulan),
      masa_iuran: formatThousand(asset.masa_iuran),
      cicilan_per_bulan: formatThousand(asset.cicilan_per_bulan),
      harga_mobil: formatThousand(asset.harga_mobil),
      images: asset.images || [],
    });
    setAssetDialog({ open: true, type: asset.type, editing: asset });
  };

  const uploadCarImages = async (assetId, files) => {
    const urls = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append('file', f);
      const res = await api.post(`/upload-image?image_type=car&asset_id=${assetId}`, fd);
      if (res.data?.url) urls.push(res.data.url);
    }
    return urls;
  };

  const submitAsset = async () => {
    const { type, editing } = assetDialog;
    if (submitting) return;
    setSubmitting(true);
    try {
      let payload;
      if (type === 'RUMAH') {
        if (!assetForm.name || !assetForm.name.trim()) { toast.error('Nama rumah wajib diisi'); return; }
        payload = { type, name: assetForm.name.trim(), masa_iuran: parseNumber(assetForm.masa_iuran) };
      } else {
        if (!assetForm.car_type || !assetForm.plat) { toast.error('Type dan Nomor Plat wajib diisi'); return; }
        payload = {
          type,
          car_type: assetForm.car_type,
          plat: assetForm.plat,
          masa_bulan: parseNumber(assetForm.masa_bulan),
          cicilan_per_bulan: parseNumber(assetForm.cicilan_per_bulan),
          harga_mobil: parseNumber(assetForm.harga_mobil),
          status: assetForm.status || 'Berjalan',
          nama_ktp: assetForm.nama_ktp || '',
          nomor_ktp: assetForm.nomor_ktp || '',
          nama_cabang: assetForm.nama_cabang || '',
        };
      }
      const keptImages = assetForm.images || [];
      let assetId;
      if (editing) {
        const { type: _t, ...updateBody } = payload;
        if (type === 'MOBIL') updateBody.images = keptImages;
        await api.put(`/assets/${editing.id}`, updateBody);
        assetId = editing.id;
      } else {
        const res = await api.post('/assets', payload);
        assetId = res.data.id;
      }
      // Upload any newly selected car images to S3, then persist URLs
      if (type === 'MOBIL' && carFiles.length > 0) {
        setUploadingImages(true);
        try {
          const newUrls = await uploadCarImages(assetId, carFiles);
          await api.put(`/assets/${assetId}`, { images: [...keptImages, ...newUrls] });
        } finally {
          setUploadingImages(false);
        }
      }
      toast.success(editing ? 'Asset berhasil diperbarui' : 'Asset berhasil ditambahkan');
      setCarFiles([]);
      setAssetDialog({ open: false, type, editing: null });
      fetchAssets();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Gagal menyimpan asset'));
    } finally {
      setSubmitting(false);
    }
  };

  const deleteAsset = async (asset) => {
    if (!window.confirm(`Hapus asset "${asset.name || `${asset.car_type} ${asset.plat}`}" beserta seluruh datanya?`)) return;
    try {
      await api.delete(`/assets/${asset.id}`);
      toast.success('Asset berhasil dihapus');
      if (selected?.id === asset.id) setSelected(null);
      fetchAssets();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Gagal menghapus asset'));
    }
  };

  // ---------- Entry (row) CRUD ----------
  const openAddEntry = () => {
    setEntryForm(selected.type === 'RUMAH'
      ? { tanggal: new Date().toISOString().slice(0, 10), angsuran: '', renovasi: '', poti: '', keterangan: '' }
      : { tanggal: new Date().toISOString().slice(0, 10), jenis: 'CICILAN', debet: '', kredit: '', keterangan: '' });
    setEntryDialog({ open: true, editing: null });
  };

  const openEditEntry = (entry) => {
    if (selected.type === 'RUMAH') {
      setEntryForm({
        ...entry,
        angsuran: formatThousand(entry.angsuran),
        renovasi: formatThousand(entry.renovasi),
        poti: formatThousand(entry.poti),
      });
    } else {
      setEntryForm({
        ...entry,
        jenis: entry.jenis || 'CICILAN',
        debet: formatThousand(entry.debet),
        kredit: formatThousand(entry.kredit),
      });
    }
    setEntryDialog({ open: true, editing: entry });
  };

  const submitEntry = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      if (!entryForm.tanggal) { toast.error('Tanggal wajib diisi'); return; }
      let body;
      if (selected.type === 'RUMAH') {
        body = {
          tanggal: entryForm.tanggal,
          angsuran: parseNumber(entryForm.angsuran),
          renovasi: parseNumber(entryForm.renovasi),
          poti: parseNumber(entryForm.poti),
          keterangan: entryForm.keterangan || '',
        };
      } else {
        body = {
          tanggal: entryForm.tanggal,
          jenis: entryForm.jenis || 'CICILAN',
          debet: parseNumber(entryForm.debet),
          kredit: parseNumber(entryForm.kredit),
          keterangan: entryForm.keterangan || '',
        };
      }
      if (entryDialog.editing) {
        await api.put(`/assets/${selected.id}/entries/${entryDialog.editing.id}`, body);
        toast.success('Baris berhasil diperbarui');
      } else {
        await api.post(`/assets/${selected.id}/entries`, body);
        toast.success('Baris berhasil ditambahkan');
      }
      setEntryDialog({ open: false, editing: null });
      fetchAssets();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Gagal menyimpan baris'));
    } finally {
      setSubmitting(false);
    }
  };

  const deleteEntry = async (entry) => {
    if (!window.confirm('Hapus baris ini?')) return;
    try {
      await api.delete(`/assets/${selected.id}/entries/${entry.id}`);
      toast.success('Baris berhasil dihapus');
      fetchAssets();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Gagal menghapus baris'));
    }
  };

  const sortedEntries = (asset) => [...(asset.entries || [])].sort((a, b) => (a.tanggal || '').localeCompare(b.tanggal || ''));

  const rumahTotals = (asset) => (asset.entries || []).reduce((acc, e) => ({
    angsuran: acc.angsuran + (e.angsuran || 0),
    renovasi: acc.renovasi + (e.renovasi || 0),
    poti: acc.poti + (e.poti || 0),
  }), { angsuran: 0, renovasi: 0, poti: 0 });

  // Mobil: Sisa Cicilan runs from Harga Mobil, each row = prev - debet + kredit
  const mobilSisaCicilan = (asset) =>
    (asset.entries || []).reduce((s, e) => s - (e.debet || 0) + (e.kredit || 0), asset.harga_mobil || 0);

  // Mobil: Sisa Masa = masa_bulan - number of 'Pembayaran Cicilan' (CICILAN) transactions
  const mobilSisaMasa = (asset) => {
    const paid = (asset.entries || []).filter((e) => (e.jenis || 'CICILAN') === 'CICILAN').length;
    return Math.max((asset.masa_bulan || 0) - paid, 0);
  };

  // Status auto-Lunas when the remaining term hits 0 (otherwise use stored status)
  const mobilStatus = (asset) => ((asset.masa_bulan || 0) > 0 && mobilSisaMasa(asset) <= 0) ? 'Lunas' : (asset.status || 'Berjalan');

  // ---------- Detail views ----------
  const renderRumahDetail = () => {
    const entries = sortedEntries(selected);
    const totals = entries.reduce((acc, e) => ({
      angsuran: acc.angsuran + (e.angsuran || 0),
      renovasi: acc.renovasi + (e.renovasi || 0),
      poti: acc.poti + (e.poti || 0),
    }), { angsuran: 0, renovasi: 0, poti: 0 });
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between py-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <Home size={18} /> {selected.name}
            <span className="text-xs font-medium px-2 py-0.5 rounded bg-teal-100 text-teal-700" data-testid="rumah-detail-masa-iuran">
              Masa Iuran: {selected.masa_iuran || 0} bln
            </span>
          </CardTitle>
          <Button size="sm" onClick={openAddEntry} data-testid="add-entry-btn"><Plus size={16} className="mr-1" /> Tambah Data</Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Tanggal</TableHead>
                <TableHead className="text-right text-xs">Angsuran</TableHead>
                <TableHead className="text-right text-xs">Renovasi</TableHead>
                <TableHead className="text-right text-xs">Poti</TableHead>
                <TableHead className="text-xs">Keterangan</TableHead>
                <TableHead className="text-right text-xs">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((e) => (
                <TableRow key={e.id} data-testid={`rumah-row-${e.id}`}>
                  <TableCell className="text-xs">{e.tanggal ? new Date(e.tanggal).toLocaleDateString('id-ID') : '-'}</TableCell>
                  <TableCell className="text-right text-xs">{formatCurrency(e.angsuran)}</TableCell>
                  <TableCell className="text-right text-xs">{formatCurrency(e.renovasi)}</TableCell>
                  <TableCell className="text-right text-xs">{formatCurrency(e.poti)}</TableCell>
                  <TableCell className="text-xs">{e.keterangan || '-'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEditEntry(e)}><Edit size={14} /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => deleteEntry(e)}><Trash2 size={14} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {entries.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-slate-400 italic py-6 text-sm">Belum ada data</TableCell></TableRow>
              )}
            </TableBody>
            {entries.length > 0 && (
              <TableFooter>
                <TableRow className="bg-teal-50">
                  <TableCell className="text-xs font-bold text-teal-900">TOTAL</TableCell>
                  <TableCell className="text-right text-xs font-bold text-teal-900">{formatCurrency(totals.angsuran)}</TableCell>
                  <TableCell className="text-right text-xs font-bold text-teal-900">{formatCurrency(totals.renovasi)}</TableCell>
                  <TableCell className="text-right text-xs font-bold text-teal-900">{formatCurrency(totals.poti)}</TableCell>
                  <TableCell colSpan={2}></TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </CardContent>
      </Card>
    );
  };

  const renderMobilDetail = () => {
    const entries = sortedEntries(selected);
    const hargaMobil = selected.harga_mobil || 0;
    // Running Sisa Cicilan: start at Harga Mobil, each row = prev - debet + kredit
    let running = hargaMobil;
    const rows = entries.map((e) => {
      running = running - (e.debet || 0) + (e.kredit || 0);
      return { ...e, sisa: running };
    });
    const sisaMasa = mobilSisaMasa(selected);
    const images = selected.images || [];
    const status = mobilStatus(selected);
    return (
      <Card>
        <CardHeader className="py-4">
          <CardTitle className="text-lg flex items-center gap-2"><Car size={18} /> {selected.car_type} - {selected.plat}</CardTitle>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-1 mt-3 text-sm text-slate-600">
            {/* Left grid */}
            <div className="space-y-1">
              <div><span className="text-slate-400">Type:</span> {selected.car_type}</div>
              <div><span className="text-slate-400">Nomor Plat:</span> {selected.plat}</div>
              <div className="flex items-center gap-1">
                <span className="text-slate-400">Status:</span>
                <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${status === 'Lunas' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`} data-testid="mobil-detail-status">{status}</span>
              </div>
            </div>
            {/* Middle grid */}
            <div className="space-y-1">
              <div><span className="text-slate-400">Nama Cabang:</span> {selected.nama_cabang || '-'}</div>
              <div><span className="text-slate-400">Nama KTP:</span> {selected.nama_ktp || '-'}</div>
              <div><span className="text-slate-400">No. KTP:</span> {selected.nomor_ktp || '-'}</div>
            </div>
            {/* Right grid */}
            <div className="space-y-1">
              <div className="font-semibold text-slate-800"><span className="text-slate-400 font-normal">Harga Mobil:</span> {formatCurrency(selected.harga_mobil || 0)}</div>
              <div><span className="text-slate-400">Cicilan/Bulan:</span> {formatCurrency(selected.cicilan_per_bulan)}</div>
              <div><span className="text-slate-400">Masa:</span> {selected.masa_bulan} Bulan</div>
              <div><span className="text-slate-400">Sisa Masa:</span> {sisaMasa} Bulan</div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Tanggal</TableHead>
                <TableHead className="text-xs">Jenis</TableHead>
                <TableHead className="text-right text-xs">Debet</TableHead>
                <TableHead className="text-right text-xs">Kredit</TableHead>
                <TableHead className="text-right text-xs">Sisa Cicilan</TableHead>
                <TableHead className="text-xs">Keterangan</TableHead>
                <TableHead className="text-right text-xs">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((e) => (
                <TableRow key={e.id} data-testid={`mobil-row-${e.id}`}>
                  <TableCell className="text-xs">{e.tanggal ? new Date(e.tanggal).toLocaleDateString('id-ID') : '-'}</TableCell>
                  <TableCell className="text-xs">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${(e.jenis || 'CICILAN') === 'CICILAN' ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-600'}`}>
                      {(e.jenis || 'CICILAN') === 'CICILAN' ? 'Cicilan' : 'Lainnya'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right text-xs text-red-600">{e.debet ? formatCurrency(e.debet) : '-'}</TableCell>
                  <TableCell className="text-right text-xs text-green-600">{e.kredit ? formatCurrency(e.kredit) : '-'}</TableCell>
                  <TableCell className="text-right text-xs font-semibold">{formatCurrency(e.sisa)}</TableCell>
                  <TableCell className="text-xs">{e.keterangan || '-'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEditEntry(e)}><Edit size={14} /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => deleteEntry(e)}><Trash2 size={14} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-400 italic py-6 text-sm">Belum ada transaksi</TableCell></TableRow>
              )}
            </TableBody>
          </Table>

          {images.length > 0 && (
            <div className="mt-5">
              <div className="text-xs font-semibold text-slate-500 mb-2">Gambar Mobil ({images.length})</div>
              <div className="flex flex-wrap gap-2" data-testid="mobil-detail-images">
                {images.map((url, i) => (
                  <img
                    key={i}
                    src={url}
                    alt={`Mobil ${i + 1}`}
                    className="h-24 w-32 object-cover rounded-md border cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => setImageModal({ open: true, index: i })}
                    data-testid={`mobil-image-thumb-${i}`}
                  />
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderAssetGrid = (list, type) => {
    // Grand total across all houses (Rumah tab only)
    const grand = type === 'RUMAH'
      ? list.reduce((acc, a) => {
          const t = rumahTotals(a);
          return { angsuran: acc.angsuran + t.angsuran, renovasi: acc.renovasi + t.renovasi, poti: acc.poti + t.poti };
        }, { angsuran: 0, renovasi: 0, poti: 0 })
      : null;
    // Summary across all cars (Mobil tab only)
    // Total Sisa Cicilan = sum of (Sisa Masa x Cicilan/Bulan) per car
    const mobilSummary = type === 'MOBIL'
      ? list.reduce((acc, a) => ({
          sisaCicilan: acc.sisaCicilan + mobilSisaMasa(a) * (a.cicilan_per_bulan || 0),
          hargaMobil: acc.hargaMobil + (a.harga_mobil || 0),
          jumlah: acc.jumlah + 1,
        }), { sisaCicilan: 0, hargaMobil: 0, jumlah: 0 })
      : null;
    return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={() => openAddAsset(type)} data-testid={`add-${type.toLowerCase()}-btn`}>
          <Plus size={16} className="mr-1" /> Tambah {type === 'RUMAH' ? 'Rumah' : 'Mobil'}
        </Button>
      </div>

      {type === 'RUMAH' && list.length > 0 && (
        <Card className="mb-4 border-teal-200 bg-teal-50/50" data-testid="rumah-grand-total">
          <CardContent className="p-4">
            <div className="text-xs font-semibold text-teal-800 mb-2">TOTAL KESELURUHAN SEMUA RUMAH</div>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div><span className="text-slate-500 text-xs block">Angsuran</span><span className="font-bold text-slate-800">{formatCurrency(grand.angsuran)}</span></div>
              <div><span className="text-slate-500 text-xs block">Renovasi</span><span className="font-bold text-slate-800">{formatCurrency(grand.renovasi)}</span></div>
              <div><span className="text-slate-500 text-xs block">Poti</span><span className="font-bold text-slate-800">{formatCurrency(grand.poti)}</span></div>
            </div>
          </CardContent>
        </Card>
      )}

      {type === 'MOBIL' && list.length > 0 && (
        <Card className="mb-4 border-teal-200 bg-teal-50/50" data-testid="mobil-summary">
          <CardContent className="p-4">
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div><span className="text-slate-500 text-xs block">Total Harga Mobil</span><span className="font-bold text-slate-800">{formatCurrency(mobilSummary.hargaMobil)}</span></div>
              <div><span className="text-slate-500 text-xs block">Total Sisa Cicilan</span><span className="font-bold text-slate-800">{formatCurrency(mobilSummary.sisaCicilan)}</span></div>
              <div><span className="text-slate-500 text-xs block">Jumlah Mobil</span><span className="font-bold text-slate-800">{mobilSummary.jumlah}</span></div>
            </div>
          </CardContent>
        </Card>
      )}

      {list.length === 0 ? (
        <div className="text-center text-slate-400 italic py-16">Belum ada {type === 'RUMAH' ? 'rumah' : 'mobil'}. Klik "Tambah" untuk menambahkan.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((asset) => {
            const t = type === 'RUMAH' ? rumahTotals(asset) : null;
            return (
            <Card key={asset.id} className="hover:shadow-md transition-shadow" data-testid={`asset-card-${asset.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div onClick={() => setSelected(asset)} className="flex-1 cursor-pointer">
                    {type === 'RUMAH' ? (
                      <>
                        <div className="flex items-center gap-2 font-semibold text-slate-800"><Home size={16} className="text-teal-600" /> {asset.name}</div>
                        <div className="text-xs text-slate-500 mt-1" data-testid={`rumah-card-masa-${asset.id}`}>
                          {(asset.entries || []).length} data · Masa Iuran: {asset.masa_iuran || 0} bln
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 font-semibold text-slate-800 flex-wrap">
                          <Car size={16} className="text-teal-600" /> {asset.car_type}
                          {(() => {
                            const st = mobilStatus(asset);
                            return (
                              <span
                                className={`text-xs font-medium px-2 py-0.5 rounded ${st === 'Lunas' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}
                                data-testid={`mobil-card-status-${asset.id}`}
                              >
                                {st}
                              </span>
                            );
                          })()}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">{asset.nama_cabang ? asset.nama_cabang + ' · ' : ''}{asset.plat} · {asset.masa_bulan} bln · {formatCurrency(asset.cicilan_per_bulan)}/bln</div>
                      </>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEditAsset(asset)}><Edit size={14} /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => deleteAsset(asset)}><Trash2 size={14} /></Button>
                  </div>
                </div>

                {type === 'RUMAH' && (
                  <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t text-xs" data-testid={`rumah-card-totals-${asset.id}`}>
                    <div><span className="text-slate-400 block">Angsuran</span><span className="font-semibold text-slate-700">{formatCurrency(t.angsuran)}</span></div>
                    <div><span className="text-slate-400 block">Renovasi</span><span className="font-semibold text-slate-700">{formatCurrency(t.renovasi)}</span></div>
                    <div><span className="text-slate-400 block">Poti</span><span className="font-semibold text-slate-700">{formatCurrency(t.poti)}</span></div>
                  </div>
                )}

                {type === 'MOBIL' && (
                  <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t text-xs" data-testid={`mobil-card-info-${asset.id}`}>
                    <div><span className="text-slate-400 block">Sisa Cicilan</span><span className="font-semibold text-slate-700">{formatCurrency(mobilSisaCicilan(asset))}</span></div>
                    <div><span className="text-slate-400 block">Sisa Masa</span><span className="font-semibold text-slate-700">{mobilSisaMasa(asset)} Bulan</span></div>
                  </div>
                )}

                <Button variant="outline" size="sm" className="w-full mt-3 text-xs" onClick={() => setSelected(asset)} data-testid={`open-asset-${asset.id}`}>Buka Detail</Button>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}
    </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="assets-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Asset</h1>
        <p className="text-slate-500 mt-1 text-sm">Kelola asset Rumah dan Mobil beserta cicilannya</p>
      </div>

      {selected ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => setSelected(null)} data-testid="back-to-list-btn">
              <ChevronLeft size={16} className="mr-1" /> Kembali ke daftar {selected.type === 'RUMAH' ? 'Rumah' : 'Mobil'}
            </Button>
            {selected.type === 'MOBIL' && (
              <Button size="sm" onClick={openAddEntry} data-testid="add-entry-btn">
                <Plus size={16} className="mr-1" /> Tambah Transaksi
              </Button>
            )}
          </div>
          {selected.type === 'RUMAH' ? renderRumahDetail() : renderMobilDetail()}
        </div>
      ) : (
        <Tabs defaultValue={initialTab} className="w-full">
          <TabsList>
            <TabsTrigger value="rumah" data-testid="tab-rumah"><Home size={16} className="mr-1" /> Rumah</TabsTrigger>
            <TabsTrigger value="mobil" data-testid="tab-mobil"><Car size={16} className="mr-1" /> Mobil</TabsTrigger>
          </TabsList>
          <TabsContent value="rumah" className="mt-4">{renderAssetGrid(rumahList, 'RUMAH')}</TabsContent>
          <TabsContent value="mobil" className="mt-4">{renderAssetGrid(mobilList, 'MOBIL')}</TabsContent>
        </Tabs>
      )}

      {/* Asset header dialog */}
      <Dialog open={assetDialog.open} onOpenChange={(o) => setAssetDialog({ ...assetDialog, open: o })}>
        <DialogContent className="max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{assetDialog.editing ? 'Edit' : 'Tambah'} {assetDialog.type === 'RUMAH' ? 'Rumah' : 'Mobil'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {assetDialog.type === 'RUMAH' ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Nama Rumah</Label>
                  <Input value={assetForm.name || ''} onChange={(e) => setAssetForm({ ...assetForm, name: e.target.value })} data-testid="input-rumah-name" placeholder="Contoh: Rumah BSD" />
                </div>
                <div>
                  <Label className="text-xs">Masa Iuran (Bulan)</Label>
                  <Input inputMode="numeric" value={assetForm.masa_iuran ?? ''} onChange={(e) => setAssetForm({ ...assetForm, masa_iuran: formatThousand(e.target.value) })} data-testid="input-rumah-masa-iuran" placeholder="Contoh: 60" />
                </div>
              </div>
            ) : (
              <>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Type</Label><Input value={assetForm.car_type || ''} onChange={(e) => setAssetForm({ ...assetForm, car_type: e.target.value })} data-testid="input-mobil-type" /></div>
                <div><Label className="text-xs">Nomor Plat</Label><Input value={assetForm.plat || ''} onChange={(e) => setAssetForm({ ...assetForm, plat: e.target.value })} data-testid="input-mobil-plat" /></div>
                <div><Label className="text-xs">Nama Cabang</Label><Input value={assetForm.nama_cabang || ''} onChange={(e) => setAssetForm({ ...assetForm, nama_cabang: e.target.value })} data-testid="input-mobil-cabang" placeholder="Bebas ketik" /></div>
                <div><Label className="text-xs">Harga Mobil</Label><Input inputMode="numeric" value={assetForm.harga_mobil ?? ''} onChange={(e) => setAssetForm({ ...assetForm, harga_mobil: formatThousand(e.target.value) })} data-testid="input-mobil-harga" placeholder="Rp" /></div>
                <div><Label className="text-xs">Masa (Bulan)</Label><Input inputMode="numeric" value={assetForm.masa_bulan ?? ''} onChange={(e) => setAssetForm({ ...assetForm, masa_bulan: formatThousand(e.target.value) })} data-testid="input-mobil-masa" /></div>
                <div><Label className="text-xs">Cicilan / Bulan</Label><Input inputMode="numeric" value={assetForm.cicilan_per_bulan ?? ''} onChange={(e) => setAssetForm({ ...assetForm, cicilan_per_bulan: formatThousand(e.target.value) })} data-testid="input-mobil-cicilan" placeholder="Rp" /></div>
                <div><Label className="text-xs">Nama KTP</Label><Input value={assetForm.nama_ktp || ''} onChange={(e) => setAssetForm({ ...assetForm, nama_ktp: e.target.value })} data-testid="input-mobil-namaktp" /></div>
                <div><Label className="text-xs">Nomor KTP</Label><Input value={assetForm.nomor_ktp || ''} onChange={(e) => setAssetForm({ ...assetForm, nomor_ktp: e.target.value })} data-testid="input-mobil-nomorktp" /></div>
                <div>
                  <Label className="text-xs">Status</Label>
                  <RadioGroup
                    value={assetForm.status || 'Berjalan'}
                    onValueChange={(val) => setAssetForm({ ...assetForm, status: val })}
                    className="flex flex-row gap-4 mt-2"
                    data-testid="mobil-status-radio"
                  >
                    <div className="flex items-center gap-1.5">
                      <RadioGroupItem value="Berjalan" id="status-berjalan" data-testid="status-berjalan" />
                      <Label htmlFor="status-berjalan" className="text-xs cursor-pointer">Berjalan</Label>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <RadioGroupItem value="Lunas" id="status-lunas" data-testid="status-lunas" />
                      <Label htmlFor="status-lunas" className="text-xs cursor-pointer">Lunas</Label>
                    </div>
                  </RadioGroup>
                </div>
              </div>

              {/* Image upload (multiple, auto-compressed & stored on S3) */}
              <div>
                <Label className="text-xs">Gambar Mobil (bisa lebih dari satu)</Label>
                <label className="mt-1 flex items-center gap-2 border border-dashed rounded-md px-3 py-2 cursor-pointer text-sm text-slate-500 hover:bg-slate-50" data-testid="mobil-image-dropzone">
                  <Upload size={16} /> Pilih gambar...
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    data-testid="input-mobil-images"
                    onChange={(e) => { setCarFiles([...carFiles, ...Array.from(e.target.files || [])]); e.target.value = ''; }}
                  />
                </label>
                {(assetForm.images?.length > 0 || carFiles.length > 0) && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(assetForm.images || []).map((url, i) => (
                      <div key={`ex-${i}`} className="relative">
                        <img src={url} alt="" className="h-16 w-20 object-cover rounded border" />
                        <button type="button" onClick={() => setAssetForm({ ...assetForm, images: assetForm.images.filter((_, idx) => idx !== i) })} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5" data-testid={`remove-existing-image-${i}`}><X size={12} /></button>
                      </div>
                    ))}
                    {carFiles.map((f, i) => (
                      <div key={`new-${i}`} className="relative">
                        <img src={URL.createObjectURL(f)} alt="" className="h-16 w-20 object-cover rounded border" />
                        <button type="button" onClick={() => setCarFiles(carFiles.filter((_, idx) => idx !== i))} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5" data-testid={`remove-new-image-${i}`}><X size={12} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssetDialog({ ...assetDialog, open: false })}>Batal</Button>
            <Button onClick={submitAsset} disabled={uploadingImages || submitting} data-testid="submit-asset-btn">{uploadingImages ? 'Mengupload gambar...' : (submitting ? 'Menyimpan...' : 'Simpan')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Entry dialog */}
      <Dialog open={entryDialog.open} onOpenChange={(o) => setEntryDialog({ ...entryDialog, open: o })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{entryDialog.editing ? 'Edit' : 'Tambah'} {selected?.type === 'RUMAH' ? 'Data' : 'Transaksi'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Tanggal</Label><Input type="date" value={entryForm.tanggal || ''} onChange={(e) => setEntryForm({ ...entryForm, tanggal: e.target.value })} data-testid="input-entry-tanggal" /></div>
            {selected?.type === 'RUMAH' ? (
              <>
                <div><Label className="text-xs">Angsuran</Label><Input inputMode="numeric" value={entryForm.angsuran ?? ''} onChange={(e) => setEntryForm({ ...entryForm, angsuran: formatThousand(e.target.value) })} data-testid="input-entry-angsuran" placeholder="Rp" /></div>
                <div><Label className="text-xs">Renovasi</Label><Input inputMode="numeric" value={entryForm.renovasi ?? ''} onChange={(e) => setEntryForm({ ...entryForm, renovasi: formatThousand(e.target.value) })} data-testid="input-entry-renovasi" placeholder="Rp" /></div>
                <div><Label className="text-xs">Poti</Label><Input inputMode="numeric" value={entryForm.poti ?? ''} onChange={(e) => setEntryForm({ ...entryForm, poti: formatThousand(e.target.value) })} data-testid="input-entry-poti" placeholder="Rp" /></div>
              </>
            ) : (
              <>
                <div>
                  <Label className="text-xs">Jenis Transaksi</Label>
                  <RadioGroup
                    value={entryForm.jenis || 'CICILAN'}
                    onValueChange={(val) => setEntryForm({ ...entryForm, jenis: val })}
                    className="flex flex-row gap-6 mt-1"
                    data-testid="entry-jenis-radio"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="CICILAN" id="jenis-cicilan" data-testid="jenis-cicilan" />
                      <Label htmlFor="jenis-cicilan" className="text-xs cursor-pointer">Pembayaran Cicilan</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="LAINNYA" id="jenis-lainnya" data-testid="jenis-lainnya" />
                      <Label htmlFor="jenis-lainnya" className="text-xs cursor-pointer">Lainnya</Label>
                    </div>
                  </RadioGroup>
                </div>
                <div><Label className="text-xs">Debet</Label><Input inputMode="numeric" value={entryForm.debet ?? ''} onChange={(e) => setEntryForm({ ...entryForm, debet: formatThousand(e.target.value) })} data-testid="input-entry-debet" placeholder="Rp" /></div>
                <div><Label className="text-xs">Kredit</Label><Input inputMode="numeric" value={entryForm.kredit ?? ''} onChange={(e) => setEntryForm({ ...entryForm, kredit: formatThousand(e.target.value) })} data-testid="input-entry-kredit" placeholder="Rp" /></div>
              </>
            )}
            <div><Label className="text-xs">Keterangan</Label><Input value={entryForm.keterangan || ''} onChange={(e) => setEntryForm({ ...entryForm, keterangan: e.target.value })} data-testid="input-entry-keterangan" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEntryDialog({ ...entryDialog, open: false })}>Batal</Button>
            <Button onClick={submitEntry} disabled={submitting} data-testid="submit-entry-btn">{submitting ? 'Menyimpan...' : 'Simpan'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image gallery modal (shows all images of the selected car) */}
      <Dialog open={imageModal.open} onOpenChange={(o) => setImageModal({ ...imageModal, open: o })}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="mobil-image-modal">
          <DialogHeader>
            <DialogTitle>Gambar Mobil {selected ? `- ${selected.car_type} ${selected.plat}` : ''}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(selected?.images || []).map((url, i) => (
              <a key={i} href={url} target="_blank" rel="noopener noreferrer" data-testid={`mobil-modal-image-${i}`}>
                <img src={url} alt={`Mobil ${i + 1}`} className="w-full h-auto max-h-[70vh] object-contain rounded-md border bg-slate-50" />
              </a>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Assets;
